import type { OptionsDocument, QuoteOptions } from 'paintscout'
import type { UpdateableProduct } from '../../../area-substrates'
import getProductFields from './get-product-fields'
import type { ProductFields } from './get-product-fields'

/**
 * The crew description split into the pieces it is assembled from, so a consumer can lay them out
 * itself (one line each) or drop the product lines — e.g. the work order's "Hide all products".
 * `crewDescription` is these same pieces joined with newlines.
 */
export interface CrewDescriptionParts {
  /** "{itemName} - Prep: 2 + Labor: 4" — whichever of those pieces apply. */
  details: string
  /** One entry per (de-duplicated) product, e.g. "Regal Select - Eggshell - 2.5 gal". */
  products: string[]
}

/**
 * @description Crew description and materialValues to be displayed
 * @returns crewDescription, its constituent parts, and productFields
 */
export function getCrewDescription({
  products,
  prep,
  hours,
  itemName,
  clientLabel,
  showValues,
  quoteOptions,
  substrate,
  options,
  substrateCoats
}: {
  products?: UpdateableProduct[]
  quoteOptions: QuoteOptions
  prep: number
  hours: number
  itemName: string
  clientLabel: string
  showValues?: boolean
  substrate?: boolean
  options: OptionsDocument
  /** The substrate's coats, used when a product itself doesn't carry a `coats` override. */
  substrateCoats?: number | string
}): { crewDescription: string; crewDescriptionParts: CrewDescriptionParts; productFields: ProductFields[] } {
  const descriptionComponents = []
  /* "secondLine" is the line that has the client label and/or the prep/hours */
  const secondLine = []
  const hoursComponents = []

  if (substrate) {
    // toRenderableCrewSubstrateArea filter
    if (clientLabel || (clientLabel == '' && clientLabel.trim().toLowerCase() !== itemName.trim().toLowerCase())) {
      secondLine.push(itemName)
    }
  } else {
    // toRenderableCrewAreaSubstrate filter
    if (clientLabel && clientLabel !== itemName) {
      secondLine.push(itemName)
    }
  }
  if (prep && showValues) {
    hoursComponents.push(`Prep: ${Math.round(prep * 100) / 100}`)
    hoursComponents.push(`Labor: ${Math.round(hours * 100) / 100}`)
  }

  const labourHours = hoursComponents.join(' + ')
  if (labourHours) {
    secondLine.push(labourHours)
  }

  const details = secondLine.filter(Boolean).length ? secondLine.join(' - ') : ''
  if (details) {
    descriptionComponents.push(details)
  }

  // Generate product related fields and description string
  // Combine dupe products for description
  const productFields: ProductFields[] = []
  const combinedProductFields = {}
  for (const product of products ?? []) {
    combinedProductFields[product.key] = combinedProductFields[product.key]
      ? {
          ...combinedProductFields[product.key],
          quantity: combinedProductFields[product.key]?.quantity ?? 0 + product.quantity,
          totalPrice: {
            useCustom: false,
            custom: 0,
            default:
              (combinedProductFields[product.key]?.totalPrice?.useCustom
                ? combinedProductFields[product.key]?.totalPrice?.custom
                : combinedProductFields[product.key]?.totalPrice?.default ?? 0) +
              (product?.totalPrice?.useCustom ? product.totalPrice?.custom ?? 0 : product?.totalPrice?.default ?? 0)
          },
          volume: {
            useCustom: false,
            custom: 0,
            default:
              (combinedProductFields[product.key]?.volume?.useCustom
                ? combinedProductFields[product.key]?.volume?.custom
                : combinedProductFields[product.key]?.volume?.default ?? 0) +
              (product?.volume?.useCustom ? product.volume?.custom ?? 0 : product?.volume?.default ?? 0)
          }
        }
      : product

    const { productFields: generatedFields } = getProductFields({
      product,
      quoteOptions,
      clientOptions: options,
      substrateCoats
    })
    productFields.push(generatedFields)
  }

  const productDescriptions: string[] = []
  for (const productKey of Object.keys(combinedProductFields) ?? []) {
    const product = combinedProductFields[productKey]
    const { descriptionString } = getProductFields({
      product,
      quoteOptions,
      clientOptions: options,
      substrateCoats
    })

    productDescriptions.push(descriptionString)
    descriptionComponents.push(descriptionString)
  }

  // Clean and join our description components
  // the if statement above is sometimes pushing an empty string into descriptionComponents, the following removes them
  const noEmptyDescriptionComponents = descriptionComponents.filter((elem) => elem.length > 0)
  const crewDescription: string = noEmptyDescriptionComponents.join('\n')

  return {
    crewDescription,
    crewDescriptionParts: { details, products: productDescriptions.filter((product) => product.length > 0) },
    productFields
  }
}
